import { Checkbox, IconButton, makeStyles, Theme } from '@material-ui/core'
import List from '@material-ui/core/List'
import ClearIcon from '@material-ui/icons/Clear'
import DeleteIcon from '@material-ui/icons/Delete'
import { InputAdornment } from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import classNames from 'classnames'
import dayjs from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PolicyTimeslot, PolicyTimeslot_DaysEnum } from 'src/api/gen'
import { IEntry } from 'src/components/SidePanel/Components/CheckboxesSection'

function useStyles() {
  return makeStyles((theme: Theme) => ({
    topContainer: { display: 'flex', justifyContent: 'center' },
    bottomContainer: { display: 'flex', justifyContent: 'start' },
    list: { overflow: 'hidden' },
    checkboxAndLabel: {
      height: 40,
      width: 40,
      display: 'inline-block',
    },
    label: {
      display: 'block',
      margin: 'auto',
      textAlign: 'center',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      fontSize: 13,
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: 2,
      letterSpacing: 'normal',
      color: '#192233',
    },
    checkbox: {
      display: 'block',
      height: 30,
      width: 30,
      borderRadius: 10,
    },
    clearButton: {
      '& svg': { fontSize: 14 },
    },
    removeButton: {
      color: theme.palette.error.light,
      flex: 1,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
    },
  }))()
}

interface IDays extends IEntry {
  dayOfWeek: PolicyTimeslot_DaysEnum
}

interface IProps {
  disabled: boolean
  error: boolean
  handleChangeDays: (value: PolicyTimeslot_DaysEnum[] | null) => void
  handleChangeEndTime: (value: string | null) => void
  handleChangeStartTime: (value: string | null) => void
  handleRemoveTimeSlot: () => void
  timeslot: PolicyTimeslot
}

function TimeSlot(props: IProps) {
  const { t } = useTranslation()
  const classes = useStyles()

  const entries: IDays[] = [
    {
      id: '1',
      dayOfWeek: PolicyTimeslot_DaysEnum.mon,
      label: t('Mon.'),
      description: t('Monday'),
    },
    {
      id: '2',
      dayOfWeek: PolicyTimeslot_DaysEnum.tue,
      label: t('Tue.'),
      description: t('Tuesday'),
    },
    {
      id: '3',
      dayOfWeek: PolicyTimeslot_DaysEnum.wed,
      label: t('Wed.'),
      description: t('Wednesday'),
    },
    {
      id: '4',
      dayOfWeek: PolicyTimeslot_DaysEnum.thu,
      label: t('Thu.'),
      description: t('Thursday'),
    },
    {
      id: '5',
      dayOfWeek: PolicyTimeslot_DaysEnum.fri,
      label: t('Fri.'),
      description: t('Friday'),
    },
    {
      id: '6',
      dayOfWeek: PolicyTimeslot_DaysEnum.sat,
      label: t('Sat.'),
      description: t('Saturday'),
    },
    {
      id: '7',
      dayOfWeek: PolicyTimeslot_DaysEnum.sun,
      label: t('Sun.'),
      description: t('Sunday'),
    },
  ]

  function handleCheck(checked: boolean, dayOfWeek: PolicyTimeslot_DaysEnum) {
    if (checked && !props.timeslot.days?.includes(dayOfWeek)) {
      props.timeslot.days?.push(dayOfWeek)
    }
    if (!checked && props.timeslot.days?.includes(dayOfWeek)) {
      const indexToRemove = props.timeslot.days.indexOf(dayOfWeek)
      props.timeslot.days.splice(indexToRemove, 1)
    }
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.topContainer}>
        <List dense={true} className={classes.list}>
          {entries.map(({ id, dayOfWeek, label, description }) => {
            return (
              <div className={classNames(classes.checkboxAndLabel)} title={description || label} key={id}>
                <label className={classNames(classes.label)}>{label}</label>
                <Checkbox
                  className={classNames(classes.checkbox)}
                  color={'primary'}
                  checked={props.timeslot.days?.includes(dayOfWeek)}
                  onChange={(_, checked) => {
                    handleCheck(checked, dayOfWeek)
                    props.handleChangeDays(props.timeslot.days ? props.timeslot.days : [])
                  }}
                  disabled={props.disabled}
                />
              </div>
            )
          })}
        </List>
        {props.disabled ? null : (
          <IconButton aria-label="delete" className={classes.removeButton} onClick={() => props.handleRemoveTimeSlot()}>
            <DeleteIcon />
          </IconButton>
        )}
      </div>
      <div className={classes.bottomContainer}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
          <TimePicker
            value={props.timeslot.start_time ? dayjs(props.timeslot.start_time, ['h:m a', 'H:m']) : null}
            onChange={(value) => props.handleChangeStartTime(dayjs(value).format('H:m'))}
            disabled={props.disabled}
            slots={{
              inputAdornment: () => (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.clearButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      props.handleChangeEndTime(null)
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
          <TimePicker
            value={props.timeslot.end_time ? dayjs(props.timeslot.end_time, ['h:m a', 'H:m']) : null}
            onChange={(value) => props.handleChangeEndTime(dayjs(value).format('H:m'))}
            disabled={props.disabled}
            slots={{
              inputAdornment: () => (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.clearButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      props.handleChangeEndTime(null)
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </LocalizationProvider>
      </div>
    </div>
  )
}

export default TimeSlot
